import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import * as API from '../Utils/API.js';
import { User } from '../Types/User';
import Schedule, { Layout } from '../Components/Schedule';
import * as EmailValidator from 'email-validator';
import { htmlIf } from '../Utils/HTML';
import StepsNav from '../Components/Steps';

type Props =
  { presenterUser: User
  , availability: string[]
  , demoLengthMinutes: number
  , isAnonymous: boolean
  , utmLcSource?: string
  }

enum Step
  { SelectTime
  , EnterDetails
  }

function formatDateTime(date: Date) {
  return date.toLocaleDateString('en-US',{
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  })
}

const BookDemo = (props: Props) => {
  const [currentStep, setCurrentStep] = useState(Step.SelectTime);

  const [demoGoals, setDemoGoals] = useState('');
  const [anonFirstName, setAnonFirstName] = useState('');
  const [anonLastName, setAnonLastName] = useState('');
  const [anonEmail, setAnonEmail] = useState('');
  const [anonPhone, setAnonPhone] = useState('');

  const [selectedDate, setSelectedDate] = useState<Date>(new Date(props.availability[0]));
  const [selectedTime, setSelectedTime] = useState<Date>();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });

    setShowErrors(false);
  }, [currentStep])

  function bookDemo() {
    setIsSubmitting(true);

    const postBody = {
      presenterUserId: props.presenterUser.id,
      startTimeUtc: selectedTime.toISOString(),
      utmLcSource: props.utmLcSource,
      demoGoals: demoGoals,
      email: anonEmail,
      firstName: anonFirstName,
      lastName: anonLastName,
      phoneNumber: anonPhone
    }

    API.post("demo_book_demo_path", postBody).then(function (result) {
      if (result['error']) {
        if (result['message']) {
          setErrorMessage(result['message'])
        } else {
          setErrorMessage("Something went wrong. Our team has been notified and will investigate the issue.")
        }
      } else {
        window.location.href = result['confirmationPath']
      }
      setIsSubmitting(false);
    })
  }

  const ViewSelectTimePage = () => {
    function handleNextClicked() {
      setCurrentStep(Step.EnterDetails);
    }

    function handleSelectTime(time: Date) {
      setSelectedTime(time)
      handleNextClicked();
    }

    return (
      <div className="card">
        <div className="">
          <div className="card-header border-0 py-2">
            <div className="h2 mb-0 text-dark">Book a Demo</div>
            <div>Demo with {props.presenterUser.firstName} {props.presenterUser.lastName}</div>
          </div>
          <div className="card-body py-3 border-top border-bottom">
            <div className="fs-md fw-semibold text-dark mb-2">Demo length: {props.demoLengthMinutes} minutes</div>
            <Schedule
              layout={Layout.Wide}
              availableDateTimes={props.availability}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onSelectDate={setSelectedDate}
              onSelectTime={handleSelectTime}
            />
          </div>
          <div className="card-footer d-flex border-0 justify-content-end">
            <button className="btn btn-primary px-6 ms-2 t--step-1-next t--select-time-next"
              onClick={handleNextClicked}
              disabled={!selectedTime}
            >
                Next
            </button>
          </div>
        </div>
      </div>
    )
  }

  const ViewEnterDetailsPage = () => {
    function handleNextClicked() {
      if (demoGoals.length > 10) {
        if (props.isAnonymous) {
          if (EmailValidator.validate(anonEmail) && anonFirstName.length > 0 && anonLastName.length > 0) {
            setCurrentStep(Step.SelectTime);
          } else {
            setShowErrors(true);
          }
        } else {
          setCurrentStep(Step.SelectTime);
        }
      } else {
        setShowErrors(true);
      }
    }

    return (
      <div className="card">
        <div className="">
          <div className="card-header border-0 py-2">
            <div className="h2 mb-0 text-dark">Your Information</div>
          </div>
          <div className="card-body py-3 border-top border-bottom">
            <div>
              {formatDateTime(selectedTime)}
               | 
              <a className="text-primary cursor-pointer fw-semibold text-decoration-none" onClick={() => setCurrentStep(Step.SelectTime)}>
                Edit
              </a>
            </div>
            <div className="fs-sm">Demo will be held via Zoom</div>
            {htmlIf(props.isAnonymous || true,
              <div className="mb-3 pb-2 border-bottom">
                {ViewAnonymousDetailEntry()}
              </div>
            )}
            <div className="mb-3">
              <h6 className="fw-bold mb-0">What are the challenges you are trying to solve in your business?</h6>
              <textarea
                className="form-control mt-1 t--demo-goals"
                rows={3}
                placeholder="Adding a note here helps us plan for your meeting…"
                value={demoGoals}
                onChange={(event) => setDemoGoals(event.target.value)}
              />
              {htmlIf(showErrors && !(demoGoals.length > 5),
                <div className="fs-sm mt-1 text-danger fw-bold">Please share a bit more information.</div>
              )}
            </div>
          </div>
          <div className="card-footer d-flex border-0 justify-content-between">
            <button className="btn btn-outline-primary px-4 t--step-2-back"
              key={'back-button'}
              onClick={() => setCurrentStep(Step.SelectTime)}
            >
              <i className="ai-chevron-left fs-lg me-1" />
              Back
            </button>
            <button className="btn btn-primary px-6 t--step-2-next t--confirm-demo"
              onClick={bookDemo}
              disabled={isSubmitting}
            >
                Confirm
            </button>
          </div>
        </div>
      </div>
    )
  }

  const ViewAnonymousDetailEntry = () => (
    <>
      <div className="row mt-2">
        <div className="col-md-6">
          <h6 className="fw-semibold mb-0">First name</h6>
          <input
            className="form-control mt-1 t--anon-first-name"
            placeholder="Enter first name…"
            autoComplete='given-name'
            value={anonFirstName}
            onChange={(event) => setAnonFirstName(event.target.value)}
          />
          {htmlIf(showErrors && !anonFirstName.length,
            <div className="fs-sm mt-1 text-danger fw-bold">Please enter your first name.</div>
          )}
        </div>
        <div className="col-md-6 mt-2 mt-md-0">
          <h6 className="fw-semibold mb-0">Last name</h6>
          <input
            className="form-control mt-1 t--anon-last-name"
            placeholder="Enter last name…"
            autoComplete='family-name'
            value={anonLastName}
            onChange={(event) => setAnonLastName(event.target.value)}
          />
          {htmlIf(showErrors && !anonLastName.length,
            <div className="fs-sm mt-1 text-danger fw-bold">Please enter your last name.</div>
          )}
        </div>
        <div className="col-12 mt-2">
          <h6 className="fw-semibold mb-0">Email address</h6>
          <input
            className="form-control mt-1 t--anon-email"
            placeholder="Enter email…"
            type='email'
            autoComplete='email'
            value={anonEmail}
            onChange={(event) => setAnonEmail(event.target.value)}
          />
          {htmlIf(showErrors && !EmailValidator.validate(anonEmail),
            <div className="fs-sm mt-1 text-danger fw-bold">Please enter a valid email address.</div>
          )}
        </div>
        <div className="col mt-2">
          <h6 className="fw-semibold mb-0">Phone number</h6>
          <input
            className="form-control mt-1 t--anon-phone"
            placeholder="Enter phone number…"
            type='tel'
            autoComplete='tel'
            value={anonPhone}
            onChange={(event) => setAnonPhone(event.target.value)}
          />
          <div className="fs-xs text-secondary mt-1">
            By providing your phone number, you agree to receive SMS reminders about your upcoming demo.
          </div>
        </div>
      </div>
    </>
  )

  return (
    <div className="row justify-content-center" style={{minHeight: '100vh'}}>
      <div className="col-12 col-lg-10 col-xl-9">
          <StepsNav
            numSteps={2}
            currentStepNum={currentStep}
            containerClass="mb-2"
          />
        {(() => {
          switch (currentStep) {
            case Step.SelectTime:
              return ViewSelectTimePage();
            case Step.EnterDetails:
              return ViewEnterDetailsPage();
            default:
              return null;
          }
        })() as React.ReactNode}
      </div>
    </div>
  )
}

export default BookDemo;
